import { call, put, takeLatest } from 'redux-saga/effects';
import {
  // Device Types actions
  fetchDeviceTypes,
  fetchDeviceTypesSuccess,
  fetchDeviceTypesFailure,
  fetchDeviceTypeById,
  fetchDeviceTypeByIdSuccess,
  fetchDeviceTypeByIdFailure,
  addDeviceType,
  addDeviceTypeSuccess,
  addDeviceTypeFailure,
  updateDeviceType,
  updateDeviceTypeSuccess,
  updateDeviceTypeFailure,
  deleteDeviceType,
  deleteDeviceTypeSuccess,
  deleteDeviceTypeFailure,
  bulkDeleteDeviceTypes,
  bulkDeleteDeviceTypesSuccess,
  bulkDeleteDeviceTypesFailure,
  // Verify Modes actions
  fetchVerifyModes,
  fetchVerifyModesSuccess,
  fetchVerifyModesFailure,
  fetchVerifyModeById,
  fetchVerifyModeByIdSuccess,
  fetchVerifyModeByIdFailure,
  addVerifyMode,
  addVerifyModeSuccess,
  addVerifyModeFailure,
  updateVerifyMode,
  updateVerifyModeSuccess,
  updateVerifyModeFailure,
  deleteVerifyMode,
  deleteVerifyModeSuccess,
  deleteVerifyModeFailure,
  bulkDeleteVerifyModes,
  bulkDeleteVerifyModesSuccess,
  bulkDeleteVerifyModesFailure,
} from './slice';
import * as deviceConfigService from '../../../services/deviceConfigService';

// ==================== Device Types Sagas ====================

function* fetchDeviceTypesSaga(action) {
  try {
    const { page, pageSize, sortColumn, sortDirection, filterName, filterIsActive } = action.payload || {};

    const params = {};
    if (page) params.page = page;
    if (pageSize) params.pageSize = pageSize;
    if (sortColumn) {
      const pascalSort = String(sortColumn).replace(/^[a-z]/, (m) => m.toUpperCase());
      params.sortColumn = pascalSort;
      params.sortDirection = sortDirection || 'asc';
    }
    if (filterName) {
      params.search = filterName;
      params.searchTerm = filterName;
    }
    if (filterIsActive !== undefined && filterIsActive !== null) {
      const statuses = Array.isArray(filterIsActive)
        ? filterIsActive
        : (filterIsActive === '' ? [] : [filterIsActive]);
      const normalized = statuses.map((s) => (s === true || s === 'true' || s === 1 || s === '1'));
      const unique = Array.from(new Set(normalized));
      if (unique.length === 1) {
        params.isActive = unique[0];
      }
    }

    const response = yield call(deviceConfigService.getAllDeviceTypes, params);
    yield put(fetchDeviceTypesSuccess(response));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(fetchDeviceTypesFailure(errorMessage));
  }
}

function* fetchDeviceTypeByIdSaga(action) {
  try {
    const response = yield call(deviceConfigService.getDeviceTypeById, action.payload);
    yield put(fetchDeviceTypeByIdSuccess(response));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(fetchDeviceTypeByIdFailure(errorMessage));
  }
}

function* addDeviceTypeSaga(action) {
  try {
    const response = yield call(deviceConfigService.createDeviceType, action.payload);
    yield put(addDeviceTypeSuccess({
      data: response,
      message: response.message
    }));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(addDeviceTypeFailure(errorMessage));
  }
}

function* updateDeviceTypeSaga(action) {
  try {
    const { id, ...data } = action.payload;
    const response = yield call(deviceConfigService.updateDeviceType, id, data);
    yield put(updateDeviceTypeSuccess({
      data: response,
      message: response.message
    }));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(updateDeviceTypeFailure(errorMessage));
  }
}

function* deleteDeviceTypeSaga(action) {
  try {
    const response = yield call(deviceConfigService.deleteDeviceType, action.payload);
    yield put(deleteDeviceTypeSuccess({
      message: response?.message
    }));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(deleteDeviceTypeFailure(errorMessage));
  }
}

function* bulkDeleteDeviceTypesSaga(action) {
  try {
    const response = yield call(deviceConfigService.bulkDeleteDeviceTypes, action.payload);
    yield put(bulkDeleteDeviceTypesSuccess({
      message: response?.message
    }));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(bulkDeleteDeviceTypesFailure(errorMessage));
  }
}

// ==================== Verify Modes Sagas ====================

function* fetchVerifyModesSaga(action) {
  try {
    const { page, pageSize, sortColumn, sortDirection, filterName, filterIsActive } = action.payload || {};

    const params = {};
    if (page) params.page = page;
    if (pageSize) params.pageSize = pageSize;
    if (sortColumn) {
      const pascalSort = String(sortColumn).replace(/^[a-z]/, (m) => m.toUpperCase());
      params.sortColumn = pascalSort;
      params.sortDirection = sortDirection || 'asc';
    }
    if (filterName) {
      params.search = filterName;
      params.searchTerm = filterName;
    }
    if (filterIsActive !== undefined && filterIsActive !== null) {
      const statuses = Array.isArray(filterIsActive)
        ? filterIsActive
        : (filterIsActive === '' ? [] : [filterIsActive]);
      const normalized = statuses.map((s) => (s === true || s === 'true' || s === 1 || s === '1'));
      const unique = Array.from(new Set(normalized));
      if (unique.length === 1) {
        params.isActive = unique[0];
      }
    }

    const response = yield call(deviceConfigService.getAllVerifyModes, params);
    yield put(fetchVerifyModesSuccess(response));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(fetchVerifyModesFailure(errorMessage));
  }
}

function* fetchVerifyModeByIdSaga(action) {
  try {
    const response = yield call(deviceConfigService.getVerifyModeById, action.payload);
    yield put(fetchVerifyModeByIdSuccess(response));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(fetchVerifyModeByIdFailure(errorMessage));
  }
}

function* addVerifyModeSaga(action) {
  try {
    const response = yield call(deviceConfigService.createVerifyMode, action.payload);
    yield put(addVerifyModeSuccess({
      data: response,
      message: response.message
    }));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(addVerifyModeFailure(errorMessage));
  }
}

function* updateVerifyModeSaga(action) {
  try {
    const { id, ...data } = action.payload;
    const response = yield call(deviceConfigService.updateVerifyMode, id, data);
    yield put(updateVerifyModeSuccess({
      data: response,
      message: response.message
    }));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(updateVerifyModeFailure(errorMessage));
  }
}

function* deleteVerifyModeSaga(action) {
  try {
    const response = yield call(deviceConfigService.deleteVerifyMode, action.payload);
    yield put(deleteVerifyModeSuccess({
      message: response?.message
    }));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(deleteVerifyModeFailure(errorMessage));
  }
}

function* bulkDeleteVerifyModesSaga(action) {
  try {
    const response = yield call(deviceConfigService.bulkDeleteVerifyModes, action.payload);
    yield put(bulkDeleteVerifyModesSuccess({
      message: response?.message
    }));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(bulkDeleteVerifyModesFailure(errorMessage));
  }
}

// Watcher saga
export function* watchDeviceConfigActions() {
  // Device Types
  yield takeLatest(fetchDeviceTypes.type, fetchDeviceTypesSaga);
  yield takeLatest(fetchDeviceTypeById.type, fetchDeviceTypeByIdSaga);
  yield takeLatest(addDeviceType.type, addDeviceTypeSaga);
  yield takeLatest(updateDeviceType.type, updateDeviceTypeSaga);
  yield takeLatest(deleteDeviceType.type, deleteDeviceTypeSaga);
  yield takeLatest(bulkDeleteDeviceTypes.type, bulkDeleteDeviceTypesSaga);
  // Verify Modes
  yield takeLatest(fetchVerifyModes.type, fetchVerifyModesSaga);
  yield takeLatest(fetchVerifyModeById.type, fetchVerifyModeByIdSaga);
  yield takeLatest(addVerifyMode.type, addVerifyModeSaga);
  yield takeLatest(updateVerifyMode.type, updateVerifyModeSaga);
  yield takeLatest(deleteVerifyMode.type, deleteVerifyModeSaga);
  yield takeLatest(bulkDeleteVerifyModes.type, bulkDeleteVerifyModesSaga);
}

export default watchDeviceConfigActions;
